import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../contexts/PermissionContext';
import { Loader } from '../components/Loader';

interface AuthGuardProps {
    children: React.ReactElement;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
    const { authenticated, check, accessToken, isInitializing, syncProfile } = useAuth();
    const { hasRoutePermission, getCurrentAdmin, hasPermission } = usePermission();
    const location = useLocation();
    const navigate = useNavigate();
    const [isChecking, setIsChecking] = React.useState(!authenticated);
    const [isAuthorized, setIsAuthorized] = React.useState(false);

    useEffect(() => {
        // Wait for auth initialization to complete
        if (isInitializing) {
            return;
        }

        const verifyAuth = async () => {
            // Always show loader while verifying during mounts
            setIsChecking(true);
            const isAuth = await check();

            if (!isAuth) {
                setIsAuthorized(false);
                setIsChecking(false);
                return;
            }

            // Sync profile in background without blocking
            syncProfile();

            // Extract path from URL
            const path = location.pathname.replace(/^\//, '').split('/')[0];
            const fullPath = location.pathname.replace(/^\//, '');

            // Skip permission check for common routes (default routes for all admins)
            const allowedPaths = ['dashboard', 'profile', 'change-password'];
            if (allowedPaths.includes(path) || allowedPaths.includes(fullPath)) {
                setIsAuthorized(true);
                setIsChecking(false);
                return;
            }

            const hasPermission = hasRoutePermission(fullPath);

            if (!hasPermission) {
                const admin = getCurrentAdmin();
                const menuPermissions = admin?.menu_permission || (admin as any)?.menuPermissions || [];
                const firstPermission = menuPermissions[0];
                const redirectPath = firstPermission ? getRouteFromPermission(firstPermission) : 'dashboard';
                navigate(`/${redirectPath}`, { replace: true });
                setIsAuthorized(false);
            } else {
                setIsAuthorized(true);
            }

            setIsChecking(false);
        };

        verifyAuth();
    }, [isInitializing, location.pathname, check, hasRoutePermission, getCurrentAdmin, navigate]);

    const getRouteFromPermission = (permission: string): string => {
        // Strip sub-permissions e.g. 'users.add' -> 'users'
        const basePermission = permission.split('.')[0];

        const permissionRouteMap: { [key: string]: string } = {
            'dashboard': 'dashboard',
            'admin': 'admin',
            'users': 'users',
            'category': 'category',
            'categoryValue': 'category-value',
            'product': 'product',
            'product-ratings': 'product-ratings',
            'coupons': 'coupons',
            'cms': 'cms',
            'notifications': 'notifications',
            'settings': 'settings',
            'tax': 'tax',
            'deliveryCharge': 'delivery-charge',
            'stock': 'stock',
            'orders': 'orders'
        };
        return permissionRouteMap[basePermission] || 'dashboard';
    };

    if (isInitializing || isChecking) {
        return <Loader fullScreen={true} />;
    }

    if (!authenticated) {
        return <Navigate to="/sign-in" replace />;
    }

    if (!isAuthorized) {
        return null; // Navigation is handled in useEffect
    }

    return children;
};
